import { useState, useMemo, useCallback } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import type {
    TrainingPlan,
    TrainingEnrollment,
    TrainingCompletion,
    PlanCategory,
    ComplianceStatus,
    CategoryProgress,
} from '../../types/trainingTypes';
import { TOPIC_STYLE_COLORS, DEFAULT_WORKING_DAYS, type TopicStyle } from '../../types/trainingTypes';
import { getCompletionStatusBadge, calculateNotificationDate, parseLocalDateStr, isWorkingDay } from '../../utils/trainingUtils';
import { BackButton } from '../ui';
import '../../css/UserTrainingProfilePage.css';

interface UserTrainingProfilePageProps {
    userId: string;
    onBack: () => void;
    onOpenManageEnrollment: (userId: string, userName: string) => void;
    onOpenAffidavit: (userId: string) => void;
}

/**
 * User Training Profile Page — detailed admin view of an individual user's training data.
 * Two-column layout: main content (left) + sidebar (right).
 *
 * Validates: Requirements 13.1–13.11, 21.1–21.3
 */
const UserTrainingProfilePage = ({
    userId,
    onBack,
    onOpenManageEnrollment,
    onOpenAffidavit,
}: UserTrainingProfilePageProps) => {
    const {
        plans,
        topics,
        enrollments,
        schedules,
        quizResponses,
        getUserComplianceStatus,
        getUserCompletions,
        getCategoryProgress,
        createScheduleEntry,
    } = useSafetyTrainingContext();

    const { allCompanyUsers, companyUser, companyOwner } = useUserContext();

    const currentUserId = companyUser?.id ?? companyOwner?.id ?? '';
    const isViewingOwnProfile = userId === currentUserId;

    // Find the user
    const user = useMemo(() => allCompanyUsers.find(u => u.id === userId), [allCompanyUsers, userId]);
    const userName = user?.name ?? 'Unknown User';

    // User enrollments
    const userEnrollments = useMemo<TrainingEnrollment[]>(
        () => enrollments.filter(e => e.userId === userId),
        [enrollments, userId],
    );

    // Enrolled plans
    const enrolledPlans = useMemo<TrainingPlan[]>(() => {
        const planIds = new Set(userEnrollments.map(e => e.planId));
        return plans.filter(p => planIds.has(p.id));
    }, [plans, userEnrollments]);

    // Use the first active enrolled plan as the primary plan for display
    const primaryPlan = useMemo<TrainingPlan | null>(() => {
        const activePlan = enrolledPlans.find(p => p.status === 'ACTIVE');
        return activePlan ?? enrolledPlans[0] ?? null;
    }, [enrolledPlans]);

    const primaryEnrollment = useMemo<TrainingEnrollment | null>(() => {
        if (!primaryPlan) return null;
        return userEnrollments.find(e => e.planId === primaryPlan.id) ?? null;
    }, [primaryPlan, userEnrollments]);

    // Parse categories from primary plan
    const categories = useMemo<PlanCategory[]>(() => {
        if (!primaryPlan) return [];
        try {
            return JSON.parse(primaryPlan.categories) as PlanCategory[];
        } catch {
            return [];
        }
    }, [primaryPlan]);

    // Compliance status
    const complianceStatus = useMemo<ComplianceStatus>(() => {
        if (!primaryPlan) return 'on-track';
        return getUserComplianceStatus(userId, primaryPlan.id);
    }, [primaryPlan, userId, getUserComplianceStatus]);

    // User completions for primary plan
    const userCompletions = useMemo<TrainingCompletion[]>(() => {
        if (!primaryPlan) return [];
        return getUserCompletions(userId, primaryPlan.id);
    }, [primaryPlan, userId, getUserCompletions]);

    // Category progress
    const categoryProgress = useMemo<CategoryProgress[]>(() => {
        if (!primaryPlan) return [];
        return getCategoryProgress(userId, primaryPlan.id);
    }, [primaryPlan, userId, getCategoryProgress]);

    // Quiz status per category
    const categoryQuizStatus = useMemo(() => {
        if (!primaryPlan || !primaryEnrollment) return new Map<string, { attempts: number; passed: boolean; latestAttempt?: typeof quizResponses[0] }>();
        const cycleStart = primaryEnrollment.enrolledDate;
        const statusMap = new Map<string, { attempts: number; passed: boolean; latestAttempt?: typeof quizResponses[0] }>();

        for (const cat of categories) {
            if (!cat.requiresQuiz) continue;
            const catResponses = quizResponses.filter(
                r => r.userId === userId && r.planId === primaryPlan.id && r.categoryId === cat.id && r.cycleStartDate === cycleStart
            ).sort((a, b) => b.attemptNumber - a.attemptNumber);

            if (catResponses.length > 0) {
                statusMap.set(cat.id, {
                    attempts: catResponses.length,
                    passed: catResponses.some(r => r.passed),
                    latestAttempt: catResponses[0],
                });
            }
        }
        return statusMap;
    }, [primaryPlan, primaryEnrollment, categories, quizResponses, userId]);

    // Total minutes viewed
    const totalMinutesViewed = useMemo(
        () => userCompletions.reduce((sum, c) => sum + c.timeValueMinutes, 0),
        [userCompletions],
    );

    // Percentage viewed ITD
    const percentViewed = useMemo(() => {
        if (!primaryPlan || primaryPlan.minimumTimeRequired === 0) return 0;
        return Math.min(100, Math.round((totalMinutesViewed / primaryPlan.minimumTimeRequired) * 100));
    }, [primaryPlan, totalMinutesViewed]);

    // Days remaining
    const daysRemaining = useMemo(() => {
        if (!primaryEnrollment) return 0;
        const dueDate = primaryEnrollment.nextDueDate
            ? parseLocalDateStr(primaryEnrollment.nextDueDate)
            : (() => {
                const enrolled = parseLocalDateStr(primaryEnrollment.enrolledDate);
                const due = new Date(enrolled);
                due.setDate(due.getDate() + (primaryPlan?.cycleDuration ?? 0));
                return due;
            })();
        const now = new Date();
        return Math.max(0, Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    }, [primaryEnrollment, primaryPlan]);

    // Next due date string
    const nextDueDateStr = useMemo(() => {
        if (!primaryEnrollment) return '—';
        const dueDate = primaryEnrollment.nextDueDate
            ? parseLocalDateStr(primaryEnrollment.nextDueDate)
            : (() => {
                const enrolled = parseLocalDateStr(primaryEnrollment.enrolledDate);
                const due = new Date(enrolled);
                due.setDate(due.getDate() + (primaryPlan?.cycleDuration ?? 0));
                return due;
            })();
        return dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }, [primaryEnrollment, primaryPlan]);

    // Last completion date
    const lastCompletionStr = useMemo(() => {
        if (primaryEnrollment?.lastCompletionDate) {
            return new Date(primaryEnrollment.lastCompletionDate).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
            });
        }
        return '—';
    }, [primaryEnrollment]);

    // Working days left
    const workingDaysLeft = useMemo(() => {
        if (!primaryPlan || !primaryEnrollment) return 0;
        const workingDays = primaryPlan.workingDays ?? [1, 2, 3, 4, 5];
        const dueDate = primaryEnrollment.nextDueDate
            ? parseLocalDateStr(primaryEnrollment.nextDueDate)
            : (() => {
                const enrolled = parseLocalDateStr(primaryEnrollment.enrolledDate);
                const due = new Date(enrolled);
                due.setDate(due.getDate() + primaryPlan.cycleDuration);
                return due;
            })();
        const now = new Date();
        let count = 0;
        const current = new Date(now);
        while (current <= dueDate) {
            if (workingDays.includes(current.getDay())) count++;
            current.setDate(current.getDate() + 1);
        }
        return count;
    }, [primaryPlan, primaryEnrollment]);

    // Min minutes needed per day
    const minMinutesPerDay = useMemo(() => {
        if (!primaryPlan || workingDaysLeft === 0) return 0;
        const remaining = primaryPlan.minimumTimeRequired - totalMinutesViewed;
        if (remaining <= 0) return 0;
        return Math.ceil(remaining / workingDaysLeft);
    }, [primaryPlan, workingDaysLeft, totalMinutesViewed]);

    // Training record filter
    const [recordFilter, setRecordFilter] = useState<'All' | 'Viewed' | 'Not Viewed'>('All');

    // Reschedule state
    const [rescheduleRecord, setRescheduleRecord] = useState<{
        topicId: string;
        description: string;
        date: string;
        categoryId?: string;
    } | null>(null);
    const [rescheduleDate, setRescheduleDate] = useState('');
    const [isRescheduling, setIsRescheduling] = useState(false);

    // Build permanent training record from completions + scheduled but not completed
    const trainingRecords = useMemo(() => {
        if (!primaryPlan) return [];

        // Get all schedule entries for this plan that apply to this user
        // (assignedUserId is null = everyone, or matches this specific user)
        const planSchedules = schedules.filter(s =>
            s.planId === primaryPlan.id &&
            (!s.assignedUserId || s.assignedUserId === userId)
        );

        // Build records from schedules
        return planSchedules.map(sched => {
            const completion = userCompletions.find(c => {
                const cDate = c.scheduleDate.split('T')[0];
                const sDate = sched.scheduledDate.split('T')[0];
                return cDate === sDate && (c.topicId === sched.topicId);
            });

            const topic = topics.find(t => t.topicId === sched.topicId || t.id === sched.topicId);
            const category = categories.find(cat => cat.id === sched.categoryId);

            const launched = completion?.launched ?? false;
            const markedAsViewed = completion?.markedAsViewed ?? false;
            const statusBadge = completion
                ? getCompletionStatusBadge(launched, markedAsViewed)
                : 'Not Viewed';

            return {
                date: sched.scheduledDate.split('T')[0],
                status: statusBadge,
                style: (topic?.style ?? 'Other') as TopicStyle,
                topicId: topic?.topicId ?? sched.topicId,
                description: topic?.description ?? 'Unknown Topic',
                category: category?.name ?? '—',
                minutes: topic?.timeValue ?? 0,
            };
        }).sort((a, b) => b.date.localeCompare(a.date));
    }, [primaryPlan, schedules, userCompletions, topics, categories]);

    // Filtered records
    const filteredRecords = useMemo(() => {
        if (recordFilter === 'All') return trainingRecords;
        if (recordFilter === 'Viewed') return trainingRecords.filter(r => r.status === 'Viewed');
        return trainingRecords.filter(r => r.status !== 'Viewed');
    }, [trainingRecords, recordFilter]);

    // Notification settings (local state — would persist via API in production)
    const [daysBefore, setDaysBefore] = useState(7);
    const [sendDaily, setSendDaily] = useState(false);

    // Next notification date preview
    const nextNotificationDate = useMemo(() => {
        if (!primaryEnrollment) return '—';
        const dueDate = primaryEnrollment.nextDueDate
            ? parseLocalDateStr(primaryEnrollment.nextDueDate)
            : (() => {
                const enrolled = parseLocalDateStr(primaryEnrollment.enrolledDate);
                const due = new Date(enrolled);
                due.setDate(due.getDate() + (primaryPlan?.cycleDuration ?? 0));
                return due;
            })();
        const notifDate = calculateNotificationDate(dueDate, daysBefore);
        return notifDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }, [primaryEnrollment, primaryPlan, daysBefore]);

    // Completion history (affidavit-based completions from all plans)
    const completionHistory = useMemo(() => {
        return userEnrollments
            .filter(e => e.lastCompletionDate)
            .map(e => {
                const plan = plans.find(p => p.id === e.planId);
                return {
                    date: new Date(e.lastCompletionDate!).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                    }),
                    planName: plan?.name ?? 'Unknown Plan',
                };
            })
            .sort((a, b) => b.date.localeCompare(a.date));
    }, [userEnrollments, plans]);

    // Helpers
    const getInitials = (name: string) => {
        const parts = name.trim().split(/\s+/);
        return parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : (name[0] ?? '').toUpperCase();
    };

    const getComplianceLabel = (status: ComplianceStatus): string => {
        switch (status) {
            case 'on-track': return 'On Track';
            case 'behind': return 'Behind Schedule';
            case 'at-risk': return '⚠ At Risk';
            default: return status;
        }
    };

    const getStatusBadgeClass = (status: string): string => {
        if (status === 'Viewed') return 'viewed';
        if (status === 'Marked-Not Launched') return 'marked-not-launched';
        return 'not-viewed';
    };

    const formatRecordDate = (dateStr: string): string => {
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // CSV Export
    const handleExportCSV = useCallback(() => {
        const headers = ['Date', 'Status', 'Style', 'Topic ID', 'Description', 'Category', 'Minutes'];
        const rows = trainingRecords.map(r => [
            r.date,
            r.status,
            r.style,
            r.topicId,
            `"${r.description.replace(/"/g, '""')}"`,
            r.category,
            String(r.minutes),
        ]);
        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `training-record-${userName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [trainingRecords, userName]);

    // Reschedule a missed topic — find the next available working day and create a new schedule entry
    const handleReschedule = useCallback(async () => {
        if (!rescheduleRecord || !primaryPlan || !rescheduleDate) return;
        setIsRescheduling(true);
        try {
            // Find the topic by topicId string
            const topic = topics.find(t => t.topicId === rescheduleRecord.topicId || t.id === rescheduleRecord.topicId);
            if (!topic) throw new Error('Topic not found');

            // Calculate day number for the new date
            const startDate = primaryPlan.startDate
                ? parseLocalDateStr(primaryPlan.startDate)
                : (primaryEnrollment ? parseLocalDateStr(primaryEnrollment.enrolledDate) : new Date());
            const targetDate = parseLocalDateStr(rescheduleDate);
            const dayNumber = Math.max(1, Math.ceil((targetDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1);

            await createScheduleEntry({
                companyId: topic.companyId,
                planId: primaryPlan.id,
                topicId: topic.id,
                scheduledDate: rescheduleDate,
                dayNumber,
                categoryId: rescheduleRecord.categoryId || null,
                locked: false,
                assignedUserId: userId, // per-user reschedule
            });

            setRescheduleRecord(null);
            setRescheduleDate('');
        } catch (err) {
            console.error('Failed to reschedule:', err);
        } finally {
            setIsRescheduling(false);
        }
    }, [rescheduleRecord, primaryPlan, primaryEnrollment, rescheduleDate, topics, createScheduleEntry]);

    // Suggest next available working day for rescheduling
    const suggestedRescheduleDate = useMemo(() => {
        if (!primaryPlan) return '';
        const workingDays = primaryPlan.workingDays ?? DEFAULT_WORKING_DAYS;
        const today = new Date();
        const candidate = new Date(today);
        candidate.setDate(candidate.getDate() + 1); // start from tomorrow
        for (let i = 0; i < 30; i++) {
            if (isWorkingDay(candidate, workingDays)) {
                const y = candidate.getFullYear();
                const m = String(candidate.getMonth() + 1).padStart(2, '0');
                const d = String(candidate.getDate()).padStart(2, '0');
                return `${y}-${m}-${d}`;
            }
            candidate.setDate(candidate.getDate() + 1);
        }
        return '';
    }, [primaryPlan]);

    // Set of topicIds that already have a future reschedule for this user
    const rescheduledTopicIds = useMemo(() => {
        if (!primaryPlan) return new Set<string>();
        const todayStr = (() => {
            const now = new Date();
            return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        })();
        const ids = new Set<string>();
        schedules
            .filter(s =>
                s.planId === primaryPlan.id &&
                s.assignedUserId === userId &&
                s.scheduledDate.split('T')[0] > todayStr
            )
            .forEach(s => {
                const topic = topics.find(t => t.id === s.topicId);
                if (topic) ids.add(topic.topicId);
                ids.add(s.topicId);
            });
        return ids;
    }, [primaryPlan, schedules, userId, topics]);

    // Days remaining color coding
    const getDaysRemainingClass = (): string => {
        if (daysRemaining <= 14) return 'danger';
        if (daysRemaining <= 30) return 'warning';
        return 'success';
    };

    // Min per day color coding
    const getMinPerDayClass = (): string => {
        if (minMinutesPerDay > 15) return 'danger';
        if (minMinutesPerDay > 8) return 'warning';
        return '';
    };

    return (
        <div>
            {/* Back Navigation */}
            <BackButton onClick={onBack} buttonText="Back to Safety & Training Home" />

            {/* Profile Header */}
            <div className="utp-header">
                <div className="utp-header-top">
                    <div className="utp-header-user">
                        <div className="utp-avatar">{getInitials(userName)}</div>
                        <div className="utp-user-info">
                            <h2>{userName}</h2>
                            <div className="utp-user-meta">
                                <span>{user?.role ?? user?.position ?? '—'}</span>
                                <span>ID: {user?.personalIdentification ?? user?.id?.slice(0, 8) ?? '—'}</span>
                                <span>Hired: {user?.dateOfHire
                                    ? new Date(user.dateOfHire).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                    : '—'}</span>
                                <span className={user?.archived ? 'utp-status-inactive' : 'utp-status-active'}>
                                    {user?.archived ? 'Inactive' : 'Active'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="utp-header-actions">
                        <button className="utp-btn-manage" onClick={() => onOpenManageEnrollment(userId, userName)}>
                            Manage Enrollment
                        </button>
                        <button className="utp-btn-affidavit" onClick={() => onOpenAffidavit(userId)} disabled={isViewingOwnProfile} title={isViewingOwnProfile ? 'You cannot sign your own affidavit' : ''}>
                            {isViewingOwnProfile ? 'Cannot Self-Certify' : 'Create Affidavit'}
                        </button>
                    </div>
                </div>

                {/* Enrolled Plan Pills */}
                <div className="utp-plan-pills">
                    {enrolledPlans.map(plan => (
                        <span key={plan.id} className="utp-plan-pill">{plan.name}</span>
                    ))}
                    {enrolledPlans.length === 0 && (
                        <span style={{ fontSize: '13px', color: 'var(--slate-gray)', fontStyle: 'italic' }}>
                            Not enrolled in any plans
                        </span>
                    )}
                </div>
            </div>

            {/* Plan Summary Cards */}
            <div className="utp-summary-cards">
                <div className="utp-summary-card">
                    <div className="utp-summary-card-label">Last Completion</div>
                    <div className="utp-summary-card-value">{lastCompletionStr}</div>
                    <div className="utp-summary-card-detail">
                        {primaryEnrollment?.lastCompletionDate ? 'Affidavit signed' : 'No completion yet'}
                    </div>
                </div>
                <div className="utp-summary-card">
                    <div className="utp-summary-card-label">Next Due</div>
                    <div className="utp-summary-card-value">{nextDueDateStr}</div>
                    <div className="utp-summary-card-detail">
                        {primaryEnrollment ? `${daysRemaining} days remaining` : '—'}
                    </div>
                </div>
                <div className="utp-summary-card">
                    <div className="utp-summary-card-label">% Viewed ITD</div>
                    <div className="utp-summary-card-value">{percentViewed}%</div>
                    <div className="utp-summary-card-detail">
                        {totalMinutesViewed}/{primaryPlan?.minimumTimeRequired ?? 0} min
                    </div>
                </div>
                <div className="utp-summary-card">
                    <div className="utp-summary-card-label">Status</div>
                    <div className="utp-summary-card-value">
                        <span className={`utp-status-badge ${complianceStatus}`}>
                            {getComplianceLabel(complianceStatus)}
                        </span>
                    </div>
                    <div className="utp-summary-card-detail">
                        {complianceStatus === 'on-track' ? 'Meeting pace' : 'Needs attention'}
                    </div>
                </div>
            </div>

            {/* Two-Column Content */}
            <div className="utp-content">
                {/* Left Column — Main Content */}
                <div className="utp-main">
                    {/* Time Accrued by Category */}
                    <div className="utp-card">
                        <div className="utp-card-header">
                            <h3>Time Accrued by Category</h3>
                        </div>
                        <div className="utp-card-body">
                            {categoryProgress.length > 0 ? (
                                categoryProgress.map(cat => {
                                    const planCat = categories.find(c => c.id === cat.categoryId);
                                    const quizStatus = categoryQuizStatus.get(cat.categoryId);
                                    return (
                                        <div key={cat.categoryId} className="utp-category-row">
                                            <div className="utp-category-row-header">
                                                <span className="utp-category-name">{cat.categoryName}</span>
                                                <span className="utp-category-stats">
                                                    {Math.round(cat.percentage)}% {cat.completedMinutes}/{cat.targetMinutes} min
                                                </span>
                                            </div>
                                            <div className="utp-progress-bar-track">
                                                <div
                                                    className={`utp-progress-bar-fill ${cat.percentage >= 100 ? 'complete' : ''}`}
                                                    style={{ width: `${Math.min(100, cat.percentage)}%` }}
                                                />
                                            </div>
                                            {planCat?.requiresQuiz && (
                                                <div className="utp-quiz-status">
                                                    {quizStatus?.passed ? (
                                                        <span className="utp-quiz-badge passed">✅ Quiz Passed (Attempt {quizStatus.attempts})</span>
                                                    ) : quizStatus && !quizStatus.passed ? (
                                                        <span className="utp-quiz-badge failed">❌ Quiz Failed — {quizStatus.attempts} attempt{quizStatus.attempts > 1 ? 's' : ''}</span>
                                                    ) : cat.percentage >= 100 ? (
                                                        <span className="utp-quiz-badge pending">📝 Quiz Pending</span>
                                                    ) : (
                                                        <span className="utp-quiz-badge not-ready">📝 Quiz Required (after {cat.targetMinutes} min)</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="utp-empty-state">No categories defined</div>
                            )}
                        </div>
                    </div>

                    {/* Permanent Training Record */}
                    <div className="utp-card">
                        <div className="utp-card-header">
                            <h3>Permanent Training Record</h3>
                            <div className="utp-record-filters">
                                {(['All', 'Viewed', 'Not Viewed'] as const).map(filter => (
                                    <button
                                        key={filter}
                                        className={`utp-filter-btn ${recordFilter === filter ? 'active' : ''}`}
                                        onClick={() => setRecordFilter(filter)}
                                    >
                                        {filter}
                                    </button>
                                ))}
                                <span className="utp-record-count">{filteredRecords.length} records</span>
                            </div>
                        </div>
                        <div className="utp-card-body" style={{ padding: 0 }}>
                            <table className="utp-record-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th>Style</th>
                                        <th>Topic ID</th>
                                        <th>Description</th>
                                        <th>Category</th>
                                        <th>Minutes</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRecords.length > 0 ? (
                                        filteredRecords.map((record, idx) => (
                                            <tr key={`${record.topicId}-${record.date}-${idx}`}>
                                                <td>{formatRecordDate(record.date)}</td>
                                                <td>
                                                    <span className={`utp-completion-badge ${getStatusBadgeClass(record.status)}`}>
                                                        {record.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className="utp-style-badge"
                                                        style={{ background: TOPIC_STYLE_COLORS[record.style] || '#6b7280' }}
                                                    >
                                                        {record.style}
                                                    </span>
                                                </td>
                                                <td>{record.topicId}</td>
                                                <td>{record.description}</td>
                                                <td>{record.category}</td>
                                                <td>{record.minutes}</td>
                                                <td>
                                                    {(() => {
                                                        const now = new Date();
                                                        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                                                        const isMissed = record.status !== 'Viewed' && record.date < todayStr;
                                                        const alreadyRescheduled = rescheduledTopicIds.has(record.topicId);
                                                        if (isMissed && alreadyRescheduled) {
                                                            return <span className="utp-rescheduled-label">Rescheduled</span>;
                                                        }
                                                        if (isMissed && !alreadyRescheduled) {
                                                            return (
                                                                <button
                                                                    className="utp-reschedule-btn"
                                                                    onClick={() => {
                                                                        const cat = categories.find(c => c.name === record.category);
                                                                        setRescheduleRecord({
                                                                            topicId: record.topicId,
                                                                            description: record.description,
                                                                            date: record.date,
                                                                            categoryId: cat?.id,
                                                                        });
                                                                        setRescheduleDate(suggestedRescheduleDate);
                                                                    }}
                                                                    title="Reschedule this missed topic for a future date"
                                                                >
                                                                    Reschedule
                                                                </button>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: 'var(--slate-gray)', fontStyle: 'italic' }}>
                                                No training records found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <span className="utp-export-link" onClick={handleExportCSV}>
                            Export CSV →
                        </span>
                    </div>
                </div>

                {/* Right Column — Sidebar */}
                <div className="utp-sidebar">
                    {/* Plan Details */}
                    <div className="utp-card">
                        <div className="utp-card-header">
                            <h3>Plan Details</h3>
                        </div>
                        <div className="utp-card-body">
                            {primaryPlan ? (
                                <>
                                    <div className="utp-detail-row">
                                        <span className="utp-detail-label">Plan Name</span>
                                        <span className="utp-detail-value">{primaryPlan.name}</span>
                                    </div>
                                    <div className="utp-detail-row">
                                        <span className="utp-detail-label">Standard</span>
                                        <span className="utp-detail-value">{primaryPlan.governingStandard}</span>
                                    </div>
                                    <div className="utp-detail-row">
                                        <span className="utp-detail-label">Cycle Duration</span>
                                        <span className="utp-detail-value">{primaryPlan.cycleDuration} days</span>
                                    </div>
                                    <div className="utp-detail-row">
                                        <span className="utp-detail-label">Total Required</span>
                                        <span className="utp-detail-value">
                                            {primaryPlan.minimumTimeRequired} min ({(primaryPlan.minimumTimeRequired / 60).toFixed(1)} hrs)
                                        </span>
                                    </div>
                                    <div className="utp-detail-row">
                                        <span className="utp-detail-label">Days Remaining</span>
                                        <span className={`utp-detail-value ${getDaysRemainingClass()}`}>
                                            {daysRemaining}
                                        </span>
                                    </div>
                                    <div className="utp-detail-row">
                                        <span className="utp-detail-label">Working Days Left</span>
                                        <span className="utp-detail-value">{workingDaysLeft}</span>
                                    </div>
                                    <div className="utp-detail-row">
                                        <span className="utp-detail-label">Min Needed/Day</span>
                                        <span className={`utp-detail-value ${getMinPerDayClass()}`}>
                                            {minMinutesPerDay} min
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <div className="utp-empty-state">No plan enrolled</div>
                            )}
                        </div>
                    </div>

                    {/* Email Notification */}
                    <div className="utp-card">
                        <div className="utp-card-header">
                            <h3>Email Notification</h3>
                        </div>
                        <div className="utp-card-body">
                            <div className="utp-notification-field">
                                <label>Days Before Due Date to Notify</label>
                                <input
                                    type="number"
                                    min={0}
                                    value={daysBefore}
                                    onChange={e => setDaysBefore(Math.max(0, parseInt(e.target.value) || 0))}
                                />
                            </div>
                            <div className="utp-toggle-row">
                                <span className="utp-toggle-label">Send every day until acknowledged</span>
                                <label className="utp-toggle">
                                    <input
                                        type="checkbox"
                                        checked={sendDaily}
                                        onChange={e => setSendDaily(e.target.checked)}
                                    />
                                    <span className="utp-toggle-slider" />
                                </label>
                            </div>
                            <div className="utp-notification-preview">
                                Next notification: {nextNotificationDate}
                            </div>
                        </div>
                    </div>

                    {/* Completion History */}
                    <div className="utp-card">
                        <div className="utp-card-header">
                            <h3>Completion History</h3>
                        </div>
                        <div className="utp-card-body">
                            {completionHistory.length > 0 ? (
                                completionHistory.map((item, idx) => (
                                    <div key={idx} className="utp-history-item">
                                        <span className="utp-history-date">{item.date}</span>
                                        <span className="utp-history-plan">{item.planName}</span>
                                    </div>
                                ))
                            ) : (
                                <div className="utp-empty-state">No completion history</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Reschedule Modal */}
            {rescheduleRecord && (
                <div className="utp-reschedule-overlay" onClick={() => setRescheduleRecord(null)}>
                    <div className="utp-reschedule-modal" onClick={e => e.stopPropagation()}>
                        <div className="utp-reschedule-header">
                            <h3>Reschedule Training</h3>
                            <button className="utp-reschedule-close" onClick={() => setRescheduleRecord(null)}>×</button>
                        </div>
                        <div className="utp-reschedule-body">
                            <div className="utp-reschedule-info">
                                <p className="utp-reschedule-topic-name">{rescheduleRecord.description}</p>
                                <p className="utp-reschedule-topic-meta">
                                    Topic ID: {rescheduleRecord.topicId} · Originally scheduled: {formatRecordDate(rescheduleRecord.date)}
                                </p>
                            </div>
                            <div className="utp-reschedule-note">
                                This will add a new schedule entry for this topic on the selected date.
                                The user will see it alongside any other topics scheduled for that day.
                                Topics are shown in order of priority (rescheduled items appear first).
                            </div>
                            <div className="utp-reschedule-field">
                                <label htmlFor="reschedule-date">New Date</label>
                                <input
                                    id="reschedule-date"
                                    type="date"
                                    value={rescheduleDate}
                                    onChange={e => setRescheduleDate(e.target.value)}
                                    min={(() => {
                                        const tomorrow = new Date();
                                        tomorrow.setDate(tomorrow.getDate() + 1);
                                        return `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
                                    })()}
                                />
                                {rescheduleDate && (() => {
                                    const wd = primaryPlan?.workingDays ?? DEFAULT_WORKING_DAYS;
                                    const d = parseLocalDateStr(rescheduleDate);
                                    const dayOfWeek = d.getDay();
                                    if (!wd.includes(dayOfWeek)) {
                                        const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek];
                                        return <div className="utp-reschedule-warning">{dayName} is not a working day for this plan.</div>;
                                    }
                                    return null;
                                })()}
                            </div>
                        </div>
                        <div className="utp-reschedule-footer">
                            <button className="utp-reschedule-cancel" onClick={() => setRescheduleRecord(null)}>
                                Cancel
                            </button>
                            <button
                                className="utp-reschedule-confirm"
                                onClick={handleReschedule}
                                disabled={!rescheduleDate || isRescheduling || (() => {
                                    if (!rescheduleDate || !primaryPlan) return false;
                                    const wd = primaryPlan.workingDays ?? DEFAULT_WORKING_DAYS;
                                    const d = parseLocalDateStr(rescheduleDate);
                                    return !wd.includes(d.getDay());
                                })()}
                            >
                                {isRescheduling ? 'Rescheduling...' : 'Reschedule'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserTrainingProfilePage;
